import { DialogComponent } from '@theme/dialog';

/**
 * <bundle-included-list>
 *
 * Truncates a bundle's included-product list to the first N cards (data-truncate-after)
 * with a fade over the tail. The trigger then either:
 *   - expands the list inline (desktop, or mobile when the drawer is off), or
 *   - opens a bottom-drawer with the full list (mobile, when data-mobile-drawer="true")
 *     so 30 books don't take over the mobile PDP.
 *
 * Extends DialogComponent so the drawer inherits scroll-lock, outside-click /
 * Escape close, and the shared modal-shell open/close animation. The drawer
 * body gets a CLONE of the list (built lazily on first open) — moving the live
 * list left the inline block empty until dialog:close fired (after the close
 * animation), so the cards visibly vanished and re-populated on dismiss.
 * The drawer is optional, so the dialog ref isn't required.
 */
class BundleIncludedList extends DialogComponent {
  requiredRefs = [];

  #list = null;
  #trigger = null;
  #label = null;
  #drawerBody = null;
  #cells = [];
  #cutoff = 0;
  #mql = null;
  #showAllLabel = 'Show all';
  #showLessLabel = 'Show less';

  connectedCallback() {
    super.connectedCallback();

    this.#list = this.querySelector('[data-list]');
    this.#trigger = this.querySelector('[data-trigger]');
    this.#label = this.#trigger?.querySelector('[data-label]');
    this.#drawerBody = this.querySelector('[data-drawer-body]');
    if (!this.#list || !this.#trigger || !this.#label) return;

    this.#cutoff = parseInt(this.dataset.truncateAfter || '', 10) || 0;
    this.#cells = Array.from(this.#list.children);

    // Nothing to truncate — leave the full list shown and the trigger hidden.
    if (this.#cutoff <= 0 || this.#cells.length <= this.#cutoff) {
      this.#trigger.hidden = true;
      return;
    }

    this.#showAllLabel = this.#trigger.dataset.showAllLabel || this.#showAllLabel;
    this.#showLessLabel = this.#trigger.dataset.showLessLabel || this.#showLessLabel;

    this.#trigger.addEventListener('click', this.#onTriggerClick);
    this.#mql = window.matchMedia('(max-width: 749px)');
    this.#mql.addEventListener('change', this.#onBreakpoint);
    this.addEventListener('bottom-drawer:close', this.closeDialog);

    this.#collapse();
    this.#trigger.hidden = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#trigger?.removeEventListener('click', this.#onTriggerClick);
    this.#mql?.removeEventListener('change', this.#onBreakpoint);
    this.removeEventListener('bottom-drawer:close', this.closeDialog);
  }

  // Mobile + drawer enabled + a dialog actually rendered.
  get #drawerMode() {
    return Boolean(this.#mql?.matches) && this.dataset.mobileDrawer === 'true' && Boolean(this.refs.dialog);
  }

  #onTriggerClick = () => {
    if (this.#drawerMode) {
      this.#openDrawer();
    } else if (this.dataset.state === 'expanded') {
      this.#collapse();
    } else {
      this.#expand();
    }
  };

  // Collapsed: hide the overflow cards, fade the tail, reset the trigger.
  #collapse() {
    this.#cells.forEach((cell, i) => {
      cell.hidden = i >= this.#cutoff;
    });
    this.dataset.state = 'collapsed';
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#label.textContent = `${this.#showAllLabel} (${this.#cells.length})`;
  }

  // Expanded inline: reveal every card.
  #expand() {
    this.#cells.forEach((cell) => {
      cell.hidden = false;
    });
    this.dataset.state = 'expanded';
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#label.textContent = this.#showLessLabel;
  }

  // Open the drawer with a full copy of the list. The inline (collapsed) list
  // stays in place, so nothing on the page vanishes while the drawer animates.
  #openDrawer() {
    if (!this.#drawerBody.childElementCount) {
      const clone = this.#list.cloneNode(true);
      clone.removeAttribute('data-list');
      for (const cell of clone.children) {
        cell.hidden = false;
      }
      this.#drawerBody.appendChild(clone);
    }
    this.showDialog();
    requestAnimationFrame(() => {
      const closeBtn = this.querySelector('[data-close-trigger]');
      if (closeBtn instanceof HTMLElement) closeBtn.focus({ preventScroll: true });
    });
  }

  #onBreakpoint = () => {
    // Crossing to desktop while the drawer is open is handled by
    // dialog-active-max-width (DialogComponent auto-closes it; the inline list
    // never moved). Otherwise re-assert the collapsed state for the new mode.
    if (this.refs.dialog?.open) return;
    if (this.dataset.state !== 'expanded') this.#collapse();
  };
}

if (!customElements.get('bundle-included-list')) {
  customElements.define('bundle-included-list', BundleIncludedList);
}
